import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type * as React from 'react';

import { cn } from 'src/lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
  className,
  ref,
  ...props
}: (React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  className?: string;
}) & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Overlay>>;
}) => (
  <DialogPrimitive.Overlay
    className={cn(
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/75 text-layout-foreground data-[state=closed]:animate-out data-[state=open]:animate-in',
      className,
    )}
    ref={ref}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = ({
  className,
  children,
  closable = true,
  ref,
  ...props
}: (React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  className?: string;
  closable?: boolean;
}) & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Content>>;
}) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Title className='sr-only'>Modal</DialogPrimitive.Title>
    <DialogPrimitive.Description className='sr-only'>
      Modal
    </DialogPrimitive.Description>
    <DialogPrimitive.Content
      aria-description='Modal'
      className={cn(
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 bg-card p-6 text-layout-foreground shadow-lg duration-200 focus:outline-hidden data-[state=closed]:animate-out data-[state=open]:animate-in sm:rounded-lg',
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
      {closable && (
        <DialogPrimitive.Close className='data-[state=open] absolute top-5 right-6 inline-flex h-8 w-8 items-center justify-center rounded-md bg-layout font-medium text-layout-foreground text-sm transition-colors hover:bg-secondary-default focus:outline-hidden focus:ring-2 focus:ring-primary-focus focus:ring-offset-2 focus:ring-offset-layout active:bg-secondary-hover disabled:pointer-events-none disabled:opacity-50'>
          <X className='size-4' />
          <span className='sr-only'>Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = ({
  className,
  ref,
  ...props
}: (React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  className?: string;
}) & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Title>>;
}) => (
  <DialogPrimitive.Title
    className={cn(
      'foreground font-semibold text-layout-foreground-title text-lg leading-none tracking-tight',
      className,
    )}
    ref={ref}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ref,
  ...props
}: (React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  className?: string;
}) & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Description>>;
}) => (
  <DialogPrimitive.Description
    className={cn('text-layout-foreground text-sm', className)}
    ref={ref}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
